import { useEffect, useRef, useState } from 'react'
import { isEmpty, isString } from 'lodash'

import { LIMIT_AUDIO_SECONDS, LIMIT_SIZE_AUDIO } from '../../utils/constants'

import './AudioMemory.css'

// mp4 plays in every browser, so prefer it; webm/ogg are the fallbacks (Firefox, older Chrome)
const RECORDING_TYPES = [
  { type: 'audio/mp4', ext: 'm4a' },
  { type: 'audio/webm;codecs=opus', ext: 'webm' },
  { type: 'audio/webm', ext: 'webm' },
  { type: 'audio/ogg;codecs=opus', ext: 'ogg' },
]

const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60)
  const rest = Math.floor(seconds % 60)
  return `${minutes}:${rest < 10 ? '0' : ''}${rest}`
}

const getRecordingType = () =>
  RECORDING_TYPES.find(({ type }) => window.MediaRecorder?.isTypeSupported?.(type)) || {
    type: '',
    ext: 'webm',
  }

const getDuration = (file) =>
  new Promise((resolve) => {
    const audio = document.createElement('audio')
    const url = URL.createObjectURL(file)
    const done = (duration) => {
      URL.revokeObjectURL(url)
      resolve(duration)
    }
    audio.preload = 'metadata'
    audio.onloadedmetadata = () => done(audio.duration)
    audio.onerror = () => done(null)
    audio.src = url
  })

const AudioMemory = ({ value, onChange = () => {}, idName = 'audioMemoryFile' }) => {
  const [isRecording, setIsRecording] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const [error, setError] = useState('')
  const [previewUrl, setPreviewUrl] = useState('')
  const recorderRef = useRef(null)
  const streamRef = useRef(null)
  const timerRef = useRef(null)

  useEffect(() => {
    if (!value || isString(value)) {
      return setPreviewUrl(value || '')
    }
    const url = URL.createObjectURL(value)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [value])

  const stopTracks = () => {
    clearInterval(timerRef.current)
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }

  useEffect(() => {
    return () => {
      if (recorderRef.current?.state === 'recording') {
        recorderRef.current.onstop = null
        recorderRef.current.stop()
      }
      stopTracks()
    }
  }, [])

  const stopRecording = () => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop()
    }
  }

  const startRecording = async () => {
    setError('')
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      return setError("This browser can't record audio. You can upload a recording instead.")
    }

    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (e) {
      return setError(
        e?.name === 'NotAllowedError'
          ? 'Microphone access is blocked. Allow it in your browser settings, or upload a recording instead.'
          : 'No microphone was found. You can upload a recording instead.'
      )
    }

    const { type, ext } = getRecordingType()
    const recorder = new MediaRecorder(streamRef.current, type ? { mimeType: type } : undefined)
    const chunks = []
    recorder.ondataavailable = (e) => e.data?.size && chunks.push(e.data)
    recorder.onstop = () => {
      stopTracks()
      setIsRecording(false)
      // The API's upload parser crashes on types with parameters ("audio/webm;codecs=opus")
      const mimeType = (recorder.mimeType || type || 'audio/webm').split(';')[0]
      onChange(new File(chunks, `memory.${ext}`, { type: mimeType }))
    }

    recorderRef.current = recorder
    recorder.start(1000)
    setSeconds(0)
    setIsRecording(true)

    const startedAt = Date.now()
    timerRef.current = setInterval(() => {
      const elapsed = (Date.now() - startedAt) / 1000
      setSeconds(elapsed)
      if (elapsed >= LIMIT_AUDIO_SECONDS) {
        stopRecording()
      }
    }, 250)
  }

  const uploadFile = async (e) => {
    const file = e.target?.files?.[0]
    e.target.value = ''
    if (!file) return

    setError('')
    if (file.size > LIMIT_SIZE_AUDIO) {
      return setError('That file is too large. Please choose a recording under 20 MB.')
    }
    const duration = await getDuration(file)
    if (duration === null) {
      return setError("That file doesn't look like audio this browser can play.")
    }
    if (isFinite(duration) && duration > LIMIT_AUDIO_SECONDS + 1) {
      return setError(
        `That recording is ${formatTime(duration)} long. Please keep it under ${formatTime(
          LIMIT_AUDIO_SECONDS
        )}.`
      )
    }
    onChange(file)
  }

  const hasAudio = !isEmpty(previewUrl)

  const uploadInput = (
    <input
      id={idName}
      type="file"
      accept="audio/*"
      className="audioMemoryInput"
      onChange={uploadFile}
    />
  )

  if (!isRecording && hasAudio) {
    return (
      <div className="audioMemory">
        <p className="audioMemoryTitle">Your family memory</p>
        <audio controls preload="metadata" src={previewUrl} className="audioMemoryPlayer" />
        <div className="audioMemoryActions">
          <button type="button" className="audioMemoryTextButton" onClick={startRecording}>
            Record again
          </button>
          <button
            type="button"
            className="audioMemoryTextButton audioMemoryRemove"
            onClick={() => onChange('')}
          >
            Remove
          </button>
        </div>
        {error && <p className="audioMemoryError">{error}</p>}
      </div>
    )
  }

  // A big round record button, like a voice memo app. It becomes a stop button while recording.
  return (
    <div className={`audioMemory audioMemoryRecorder ${isRecording ? 'isRecording' : ''}`}>
      <button
        type="button"
        className="audioRecordButton"
        onClick={isRecording ? stopRecording : startRecording}
        aria-label={isRecording ? 'Stop recording' : 'Start recording a family memory'}
      >
        {isRecording ? <span className="audioRecordStop" /> : <MicIcon />}
      </button>
      <div className="audioRecorderText">
        {isRecording ? (
          <>
            <p className="audioMemoryTitle" role="status">
              <span className="audioMemoryDot" /> Recording{' '}
              <span className="audioMemoryTime">
                {formatTime(seconds)} / {formatTime(LIMIT_AUDIO_SECONDS)}
              </span>
            </p>
            <p className="audioMemoryHint">Tap the button to stop.</p>
          </>
        ) : (
          <>
            <p className="audioMemoryTitle">Record a family memory</p>
            <p className="audioMemoryHint">
              Tap and tell the story behind this recipe, a tip, or a family secret. Up to{' '}
              {LIMIT_AUDIO_SECONDS / 60} minutes.
            </p>
            {uploadInput}
            <label htmlFor={idName} className="audioMemoryUpload">
              or upload a voice memo
            </label>
          </>
        )}
        {error && <p className="audioMemoryError">{error}</p>}
      </div>
    </div>
  )
}

const MicIcon = () => (
  <svg viewBox="0 0 24 24" width="30" height="30" fill="none" aria-hidden="true">
    <rect x="8.5" y="2.5" width="7" height="12" rx="3.5" fill="currentColor" />
    <path
      d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21m-3 0h6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
)

export default AudioMemory
